/** @format */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Modal } from 'antd'
import { useTranslation } from 'react-i18next'

interface BuildMeta {
  buildTime?: number
}

/**
 * Checks `/build-meta.json` on every route navigation and shows an Ant Design
 * confirm modal when a new deployment is detected.
 *
 * The first fetch seeds a baseline `buildTime`. Subsequent fetches compare
 * against that baseline; if the value changed, the user is prompted to reload.
 * A session-scoped `notifiedRef` guarantees the modal is shown at most once
 * per session (the user won't be nagged every time they change routes).
 *
 * Network errors are swallowed silently so offline users don't see errors.
 */
export function useUpdateChecker(): void {
  const { pathname } = useLocation()
  const { t, i18n } = useTranslation()
  const baselineRef = useRef<number | null>(null)
  const notifiedRef = useRef<boolean>(false)

  useEffect(() => {
    if (notifiedRef.current) return

    let cancelled = false

    fetch('/build-meta.json', { cache: 'no-cache' })
      .then((res) => (res.ok ? res.json() : null))
      .then((meta: BuildMeta | null) => {
        if (cancelled || !meta?.buildTime) return

        if (baselineRef.current === null) {
          baselineRef.current = meta.buildTime
          return
        }

        if (meta.buildTime !== baselineRef.current && !notifiedRef.current) {
          notifiedRef.current = true
          Modal.confirm({
            title: i18n.exists('update_available')
              ? t('update_available')
              : 'Yangi versiya mavjud',
            content: i18n.exists('update_description')
              ? t('update_description')
              : 'Iltimos, ilovani yangilang.',
            okText: i18n.exists('update_reload')
              ? t('update_reload')
              : 'Yangilash',
            cancelText: i18n.exists('update_later')
              ? t('update_later')
              : 'Keyinroq',
            onOk: () => window.location.reload(),
          })
        }
      })
      .catch(() => {
        // Silent: offline users shouldn't see errors
      })

    return () => {
      cancelled = true
    }
  }, [pathname, t, i18n])
}
